import React, { FC } from 'react'
import { BaseFacetProps } from '../../types'
import {
  convertDefaultToLocaleCurrency,
  formatNumberToCurrencyCommas,
  getCurrencySymbol,
} from 'components/FormatPrice/helpers'
import { updateClientSideUrl } from '../../helpers/url'
import { PriceFacetConnector, PriceFacetConnectorProps } from 'connectors/Facets'
import RadioListFacet from '../../components/RadioListFacet'
import CustomPriceFields from './CustomPriceFields'

const PRICE_RANGES = [
  {
    label: 'Under $500',
    presetMin: 0,
    presetMax: 500,
    format: 'under',
  },
  {
    label: '$500 - $1,000',
    presetMin: 500,
    presetMax: 1000,
    format: 'divider',
  },
  {
    label: '$1,000 - $2,000',
    presetMin: 1000,
    presetMax: 2000,
    format: 'divider',
  },
  {
    label: '$2,000 - $5,000',
    presetMin: 2000,
    presetMax: 5000,
    format: 'divider',
  },
  {
    label: '$5,000 - $10,000',
    presetMin: 5000,
    presetMax: 10000,
    format: 'divider',
  },
  {
    label: 'Over $10,000',
    presetMin: 10000,
    presetMax: 500000,
    format: 'over',
  },
]

type PriceAttributeObject = {
  label: string
  presetMin: number
  presetMax: number
  format: string
}

type PriceFacetProps = BaseFacetProps &
  PriceFacetConnectorProps & {
    onFilterCallback?: (hrefParam: string) => void
    facetName?: string
    attributesList?: ReadonlyArray<PriceAttributeObject>
    isCustomPriceEnabled?: boolean
  }

const PriceFacet: FC<PriceFacetProps> = ({
  baseUrl,
  nextAppRoute,
  selectedCurrency,
  selectedPrice,
  getHref,
  onFilterCallback,
  facetName = 'price',
  attributesList = PRICE_RANGES,
  priceMinMax,
  isCustomPriceEnabled,
}) => {
  const createLabel = ({ presetMin, presetMax, format, selectedCurrency }): string => {
    const currencySymbol = getCurrencySymbol(selectedCurrency)

    switch (format) {
      case 'under':
        const convertedUnderPriceInCents = convertDefaultToLocaleCurrency(
          presetMax,
          selectedCurrency
        )
        return `Under ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedUnderPriceInCents,
          selectedCurrency.locale
        )}`

      case 'over':
        const convertedOverPriceInCents = convertDefaultToLocaleCurrency(
          presetMin,
          selectedCurrency
        )
        return `Over ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedOverPriceInCents,
          selectedCurrency.locale
        )}`

      case 'divider':
      default:
        const convertedMinPriceInCents = convertDefaultToLocaleCurrency(presetMin, selectedCurrency)
        const convertedMaxPriceInCents = convertDefaultToLocaleCurrency(presetMax, selectedCurrency)
        return `${currencySymbol}${formatNumberToCurrencyCommas(
          convertedMinPriceInCents,
          selectedCurrency.locale
        )} - ${currencySymbol}${formatNumberToCurrencyCommas(
          convertedMaxPriceInCents,
          selectedCurrency.locale
        )}`
    }
  }

  const handleSizeFilterClick = (hrefParam: string) => {
    updateClientSideUrl(hrefParam, nextAppRoute)
    onFilterCallback?.(hrefParam)
  }

  const filterOptions = attributesList.map(({ presetMin, presetMax, format }) => {
    const isSelected = !!(selectedPrice && selectedPrice === `${presetMin}-${presetMax}`)
    const href = getHref({
      [facetName]: selectedPrice && isSelected ? '' : `${presetMin}-${presetMax}`,
      page: '', // Reset Page
    })
    const newUrlPath = `${baseUrl}${href}`

    return {
      text: createLabel({
        presetMin,
        presetMax,
        format,
        selectedCurrency,
      }),
      selected: isSelected,
      onClick: () => handleSizeFilterClick(newUrlPath),
      href: newUrlPath,
    }
  })

  return (
    <RadioListFacet facetName='price' filterOptions={filterOptions}>
      {isCustomPriceEnabled && (
        <CustomPriceFields
          baseUrl={baseUrl}
          nextAppRoute={nextAppRoute}
          getHref={getHref}
          priceMinMax={priceMinMax}
          selectedCurrency={selectedCurrency}
        />
      )}
    </RadioListFacet>
  )
}

export default PriceFacetConnector(PriceFacet)
